import { JSX, memo } from 'react'

import {
  CurrencyAmount,
  TokenBalance,
  TokenDisplay,
} from '~/components/component-library'
import { formatAbbreviatedTokenAmount } from '~/components/component-library/TokenBalance'
import { SupportedChainId } from '~/constants/chains'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { useDenominationStore } from '~/store/denominationStore'
import { IAddress } from '~/types'
import { getNumberFromBigNumber } from '~/utils/numbers'

// Renders a token amount as its USD value or its formatted token amount,
// following the same USD/token toggle as the "Recipient gets" field. The token
// formatter matches TokenBalance's (`formatAbbreviatedTokenAmount`) so the
// decimals shown here line up with the spend shown by TokenBalance.
const AmountDisplay = ({
  address,
  chainId,
  amount,
  hideSymbol = false,
}: {
  address: IAddress
  chainId: SupportedChainId
  amount: bigint
  // Omit the token symbol in token mode — useful where the symbol already
  // appears nearby (e.g. the TokenDisplay line directly above this amount).
  hideSymbol?: boolean
}): JSX.Element => {
  const isUsdMode = useDenominationStore((s) => s.isUsdMode)
  const metadata = useTokenMetadata({ address, chainId, includePrice: true })
  const { convertTokenToUSD } = useTokenPrices()

  // Vault positions always render as USD: raw share counts aren't
  // user-meaningful (the balance card shows positions by value too).
  if (isUsdMode || metadata?.isPosition) {
    return (
      <CurrencyAmount
        value={convertTokenToUSD({ token: address, amount, chainId })}
        layout="inline"
      />
    )
  }

  const decimal = getNumberFromBigNumber(amount, metadata?.decimals ?? 18)
  // tabular-nums to match CurrencyAmount (used in USD mode) so the available
  // amount is consistent across the USD/token toggle.
  return (
    <span className="tabular-nums">
      {formatAbbreviatedTokenAmount(decimal, { address, chainId })}
      {hideSymbol ? null : ` ${metadata?.symbol}`}
    </span>
  )
}

// A single payment-source row: a two-line TokenDisplay on the left (symbol +
// "network • <amount> available") and an outflow TokenBalance on the right for
// what you'll spend (negative, since it leaves your account).
//
// `available` = balance held; `spend` = estimated amount spent to cover the send.
//
// Memoized: it's rendered once per swap source (N rows) and the form re-renders
// on every quote poll tick; all props are primitives, so unchanged rows skip
// re-rendering (and re-running their token-metadata/price hooks).
const PaymentSourceRowInner = ({
  address,
  chainId,
  available,
  spend,
  hideAvailable = false,
  isLoadingSpend = false,
}: {
  address: IAddress
  chainId: SupportedChainId
  available: bigint
  spend: bigint
  // When the source token is the same as the token the recipient gets, the
  // "amount available" line is redundant (you're paying directly) — hide it.
  hideAvailable?: boolean
  // Quote still resolving for this (selected) source — let TokenBalance show
  // its own loading state on the spend instead of a separate affordance.
  isLoadingSpend?: boolean
}): JSX.Element => {
  return (
    <div className="flex w-full items-center justify-between gap-2">
      <TokenDisplay
        address={address}
        chainId={chainId}
        layout="two-line"
        size="small"
        secondaryLabel={
          hideAvailable ? undefined : (
            // One flex child pulled flush against the chain label (the `-ml-1.5`
            // cancels the parent's `gap-1.5`) so the separator dot keeps its own
            // tighter, equal spacing on both sides instead of the wider parent gap.
            <span className="-ml-1.5 whitespace-nowrap">
              <span aria-hidden className="mx-1">
                •
              </span>
              <AmountDisplay
                address={address}
                chainId={chainId}
                amount={available}
                hideSymbol
              />{' '}
              available
            </span>
          )
        }
      />
      <TokenBalance
        address={address}
        chainId={chainId}
        tokenAmount={spend}
        layout="standard"
        direction="outflow"
        size="small"
        isLoadingUsdValue={isLoadingSpend}
      />
    </div>
  )
}

export const PaymentSourceRow = memo(PaymentSourceRowInner)
